import type {
    BandwidthApprovalDecision,
    BandwidthRegistrationStatus,
    BandwidthTnOptionOrderStatus,
    TcrAppealStatus,
    TcrBrandRelationship,
    TcrEinIssuingCountry,
    TcrEntityTypeCode,
    TcrIdentityStatus,
    TcrMnoTerms,
    TcrOperationStatus,
    TcrPhoneNumberOrderStatus,
    TcrRejectionCategory,
    TcrSharingStatus,
    TcrVettingStatusCode,
} from '~/services/adminTenDlcRegistrationClient.types';

export const identityStatusLabels: Record<TcrIdentityStatus, string> = {
    SELF_DECLARED: 'Self-declared',
    UNVERIFIED: 'Unverified',
    VERIFIED: 'Verified',
    VETTED_VERIFIED: 'Vetted and verified',
};

export const identityStatusColors: Record<TcrIdentityStatus, 'slate' | 'info' | 'success' | 'danger'> = {
    SELF_DECLARED: 'slate',
    UNVERIFIED: 'danger',
    VERIFIED: 'info',
    VETTED_VERIFIED: 'success',
};

export const appealStatusLabels: Record<TcrAppealStatus, string> = {
    PENDING: 'Pending',
    COMPLETE: 'Complete',
};

export const brandRelationshipLabels: Record<TcrBrandRelationship, string> = {
    BASIC_ACCOUNT: 'Basic account (no business history with Voxie)',
    SMALL_ACCOUNT: 'Small account (some business history)',
    MEDIUM_ACCOUNT: 'Medium account (solid history, good standing)',
    LARGE_ACCOUNT: 'Large account (dedicated account manager, highly trusted)',
    KEY_ACCOUNT: 'Key account (dedicated account team, strategic value)',
};

export const einIssuingCountryLabels: Record<TcrEinIssuingCountry, string> = {
    CA: 'Canada (business number)',
    US: 'United States (EIN)',
};

// The brand form's labels, keyed like its fields and the 422 errors.
export const brandFieldLabels: Record<string, string> = {
    entity_type: 'Entity type',
    display_name: 'DBA / display name',
    company_name: 'Legal company name',
    ein: 'EIN',
    ein_issuing_country: 'EIN issuing country',
    country: 'Country',
    street: 'Street',
    city: 'City',
    state: 'State',
    postal_code: 'Zip',
    phone: 'Phone',
    email: 'Support email',
    website: 'Website',
    vertical: 'Vertical',
    brand_relationship: 'Brand relationship',
    stock_symbol: 'Stock symbol',
    stock_exchange: 'Stock exchange',
    business_contact_email: 'Business contact email',
    alt_business_id: 'Alternate business id',
    alt_business_id_type: 'Alternate business id type',
};

export const brandFieldLabel = (field: string): string => brandFieldLabels[field] ?? field;

export const sharingStatusLabels: Record<TcrSharingStatus, string> = {
    PENDING: 'Pending Bandwidth review',
    ACCEPTED: 'Accepted by Bandwidth',
    DECLINED: 'Declined by Bandwidth',
};

export const sharingStatusColors: Record<TcrSharingStatus, 'slate' | 'success' | 'danger'> = {
    PENDING: 'slate',
    ACCEPTED: 'success',
    DECLINED: 'danger',
};

export const operationStatusLabels: Record<TcrOperationStatus, string> = {
    REGISTERED: 'Registered',
    REVIEW: 'In review',
    REJECTED: 'Rejected',
    SUSPENDED: 'Suspended',
};

export const operationStatusColors: Record<TcrOperationStatus, 'slate' | 'success' | 'danger'> = {
    REGISTERED: 'success',
    REVIEW: 'slate',
    REJECTED: 'danger',
    SUSPENDED: 'danger',
};

export const rejectionCategoryLabels: Record<TcrRejectionCategory, string> = {
    BRAND_INCONSISTENCIES: 'Brand inconsistencies',
    CALL_TO_ACTION: 'Call to action',
    CAMPAIGN_DESCRIPTION_OR_ATTRIBUTES: 'Campaign description or attributes',
    DISALLOWED_CONTENT: 'Disallowed content',
    MANDATORY_MESSAGE_TERMINOLOGY: 'Mandatory message terminology',
    PRIVACY_POLICY: 'Privacy policy',
    SAMPLE_MESSAGE: 'Sample message',
    TERMS_AND_CONDITIONS: 'Terms and conditions',
    OTHER: 'Other',
};

export const vettingStatusCodeLabels: Record<TcrVettingStatusCode, string> = {
    PENDING: 'Pending',
    UNSCORE: 'Unscored',
    ACTIVE: 'Active',
    FAILED: 'Failed',
    EXPIRED: 'Expired',
};

export const approvalDecisionLabels: Record<BandwidthApprovalDecision, string> = {
    APPROVED: 'Approved',
    PENDING: 'Pending',
    REJECTED: 'Rejected',
    UNKNOWN: 'Unknown',
    SUSPENDED: 'Suspended',
};

export const approvalDecisionColors: Record<BandwidthApprovalDecision, 'slate' | 'success' | 'danger'> = {
    APPROVED: 'success',
    PENDING: 'slate',
    REJECTED: 'danger',
    UNKNOWN: 'slate',
    SUSPENDED: 'danger',
};

export const registrationStatusLabels: Record<BandwidthRegistrationStatus, string> = {
    PROCESSING: 'Provisioning',
    SUCCESS: 'Registered',
    FAILURE: 'Failed',
};

export const registrationStatusColors: Record<BandwidthRegistrationStatus, 'info' | 'success' | 'danger'> = {
    PROCESSING: 'info',
    SUCCESS: 'success',
    FAILURE: 'danger',
};

export const tnOptionOrderStatusLabels: Record<BandwidthTnOptionOrderStatus, string> = {
    RECEIVED: 'Received',
    PROCESSING: 'Processing',
    COMPLETE: 'Complete',
    PARTIAL: 'Partially complete',
    FAILED: 'Failed',
    EXCEPTION: 'Exception',
};

export const phoneNumberOrderStatusLabels: Record<TcrPhoneNumberOrderStatus, string> = {
    received: 'Pending',
    processing: 'Processing',
    complete: 'Complete',
    partial: 'Partially complete',
    failed: 'Failed',
    exception: 'Exception',
};

export const phoneNumberOrderStatusColors: Record<TcrPhoneNumberOrderStatus, 'info' | 'success' | 'amber' | 'danger'> =
    {
        received: 'info',
        processing: 'info',
        complete: 'success',
        partial: 'amber',
        failed: 'danger',
        exception: 'danger',
    };

// TCR MNO network ids, verified against GET /enum/mno on our account.
export const mnoLabels: Record<string, string> = {
    '10017': 'AT&T',
    '10035': 'T-Mobile',
    '10037': 'US Cellular',
    '10038': 'Verizon Wireless',
    '10538': 'Liberty',
    '10631': 'ClearSky',
    '10901': 'Interop',
};

export const MNO_ATT = '10017';
export const MNO_TMOBILE = '10035';

export const mnoLabel = (mnoId: string, fallback: string | null): string => mnoLabels[mnoId] ?? fallback ?? mnoId;

export const yesNo = (value: boolean | null): string => (null === value ? '—' : value ? 'Yes' : 'No');

export const feeLabel = (fee: number | null): string => (null === fee ? '—' : `$${fee.toFixed(2)}`);

export const carrierClassLabel = (terms: TcrMnoTerms): string => `${terms.msg_class ?? '—'} / ${terms.tpm ?? '—'}`;

export const carrierTierLabel = (terms: TcrMnoTerms): string =>
    `${terms.brand_tier ?? '—'} / ${terms.brand_daily_cap ?? '—'}`;

// TcrBrandPayload::REGISTRABLE_ENTITY_TYPES.
export const registrableEntityTypes: TcrEntityTypeCode[] = [
    'PRIVATE_PROFIT',
    'PUBLIC_PROFIT',
    'NON_PROFIT',
    'GOVERNMENT',
];

// Above 75 is the top tier, so an enhanced vet buys nothing.
export const TOP_TIER_SCORE = 75;

// The T-Mobile daily cap of the lowest, unvetted tier.
export const UNVETTED_DAILY_CAP = 2000;
